import { version } from '@root-project/package.json';
import { ZendeskConversationField } from '@zendesk/models/zendesk-conversation-field';

type ZendeskCommandQueue = (...args: unknown[]) => void;

interface WindowWithZendesk extends Window {
  zE?: ZendeskCommandQueue;
}

export type ZendeskTokenProvider = () => Promise<string | null>;

export const ZENDESK_APP_VERSION = `v${version}`;

const READY_RETRY_MS = 250;
const READY_MAX_ATTEMPTS = 40; // ~10s total

// Retries until window.zE exists, then gives up silently.
// Resolves `true` once dispatched, `false` if it gave up.
async function runWhenReady(
  run: (zE: NonNullable<WindowWithZendesk['zE']>) => void
): Promise<boolean> {
  if (typeof window === 'undefined') {
    return false;
  }

  const windowWithZendesk = window as WindowWithZendesk;

  for (let attempt = 0; attempt <= READY_MAX_ATTEMPTS; attempt++) {
    const zE = windowWithZendesk.zE;
    if (typeof zE === 'function') {
      try {
        run(zE);
      } catch {
        // Widget present but the command failed — ignore; support is optional.
      }
      return true;
    }
    if (attempt < READY_MAX_ATTEMPTS) {
      await new Promise(resolve => window.setTimeout(resolve, READY_RETRY_MS));
    }
  }
  return false;
}

export function isZendeskAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as WindowWithZendesk).zE === 'function'
  );
}

export function setZendeskConversationFields(
  fields: ZendeskConversationField[]
): void {
  const valid = fields.filter(
    f =>
      Boolean(f.id) &&
      f.value !== '' &&
      f.value !== null &&
      f.value !== undefined
  );
  if (valid.length === 0) {
    return;
  }
  void runWhenReady(zE => zE('messenger:set', 'conversationFields', valid));
}

export function setZendeskConversationTags(tags: string[]): void {
  const valid = tags.filter(Boolean);
  if (valid.length === 0) {
    return;
  }
  void runWhenReady(zE => zE('messenger:set', 'conversationTags', valid));
}

// Zendesk calls `getToken` on expiry/401 too, so it must fetch a fresh JWT
// each time. If it resolves null or throws, the widget stays anonymous.
export function loginZendeskUser(
  getToken: ZendeskTokenProvider
): Promise<boolean> {
  return runWhenReady(zE => {
    zE('messenger', 'loginUser', (callback: (jwt: string) => void) => {
      getToken()
        .then(token => {
          if (token) {
            callback(token);
          }
        })
        .catch(() => {
          // Not authenticated / fetch failed — remain anonymous.
        });
    });
  });
}

export function logoutZendeskUser(): void {
  void runWhenReady(zE => zE('messenger', 'logoutUser'));
}

export function showZendeskWidget(): void {
  void runWhenReady(zE => zE('messenger', 'show'));
}

export function hideZendeskWidget(): void {
  void runWhenReady(zE => zE('messenger', 'hide'));
}

export function openZendeskMessenger(): void {
  void runWhenReady(zE => zE('messenger', 'open'));
}

export function closeZendeskMessenger(): void {
  void runWhenReady(zE => zE('messenger', 'close'));
}

export function onZendeskUnreadMessages(
  callback: (count: number) => void
): Promise<boolean> {
  return runWhenReady(zE => zE('messenger:on', 'unreadMessages', callback));
}

export function onZendeskMessengerOpen(callback: () => void): Promise<boolean> {
  return runWhenReady(zE => zE('messenger:on', 'open', callback));
}

export function onZendeskMessengerClose(
  callback: () => void
): Promise<boolean> {
  return runWhenReady(zE => zE('messenger:on', 'close', callback));
}

// Builds a `prefix:id-name` tag (e.g. "client:1-RebelDot").
export function formatIdNameTag(
  prefix: string,
  id?: number | string | null,
  name?: string | null
): string | null {
  const safeName = name
    ? name
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[^\w-]/g, '')
    : '';
  const hasId =
    id !== null &&
    id !== undefined &&
    id !== '' &&
    !(typeof id === 'number' && Number.isNaN(id));

  if (hasId && safeName) {
    return `${prefix}:${id}-${safeName}`;
  }
  if (hasId) {
    return `${prefix}:${id}`;
  }
  if (safeName) {
    return `${prefix}:${safeName}`;
  }
  return null;
}
